/**
 * Inky's agent turn. Runs outside React so it survives panel remounts.
 *
 * Turns go to the SERVER agent first (supabase/functions/inky-agent via serverAgent.js: the
 * loop, the tools and persistence live on the server; the browser streams the reply and applies
 * client actions). When the server agent is not deployed or unreachable, the turn runs here in
 * the browser instead (the wave-1 loop below), with the same registry, cards and persistence.
 *
 * Browser loop: it saves the user message (threading the conversation id it got back, so a
 * first message never splits across two conversations), retrieves project context ONCE, builds
 * the per-turn system prompt, then alternates model rounds and tool calls until the model answers.
 *
 * Native protocol: Gemini function calling with real multi-turn contents. Every round's
 * calls run in order; results go back as functionResponse parts. Guards ported from Flint
 * (cairn lib/ai/loop.ts): anti-repeat signature, one empty-reply nudge, a closing
 * text-only round when the step budget runs out, and capped tool results.
 * Legacy protocol (old proxy): the flattened text-JSON envelope, one tool per round.
 *
 * Progress is folded into the shared turn registry (assistantTurns.js); visible text is
 * coalesced to one update per animation frame. The reply is saved with its tool trace and
 * result cards in chat_messages.tool_call ({v: 2, steps, cards}).
 */

import chatAgentService from "../services/chatAgentService";
import { stableSeed } from "../constants/aiModels";
import {
  applyTurnEvent,
  beginTurn,
  failTurn,
  finishTurn,
  markTurnStopped,
} from "./assistantTurns";
import { createFrameTextBuffer } from "./streamBuffer";
import { runServerTurn, serverAgentEnabled } from "./serverAgent";
import {
  REPLAYED_CALL_SIGNATURE,
  buildHistoryContents,
  buildLegacyTranscript,
  pushContent,
} from "./conversationContents";
import {
  TOOL_RESULT_PERSIST_CHARS,
  buildFunctionDeclarations,
  callSignature,
  capResultText,
  compactToolsForPrompt,
  findTool,
  isToolAvailable,
  toFunctionResponsePayload,
  toGeminiName,
  validateArgs,
} from "./toolSchema";
import { buildInkySystemPrompt, oneLine } from "./inkyPersona";
import { resolveActiveContext, retrieveForTurn } from "./projectGrounding";
import { snapshotEditor } from "./editorBridge";

/** Model rounds per turn (native). */
const MAX_ROUNDS = 8;
/** Model rounds per turn (legacy text-JSON protocol). */
const MAX_LEGACY_ROUNDS = 5;
/** Default title of a conversation that has not been named yet. */
export const DEFAULT_CONVERSATION_TITLE = "New Chat";

const EMPTY_RETRY_NUDGE =
  "[system] Your reply came back EMPTY: no text and no tool call reached the user. Respond now with a real reply, or make the tool call you intended. Never claim work happened that did not.";
const BUDGET_NUDGE =
  "[system] You have used the step budget for this turn. Do not call more tools. Reply to the user now with what you found and what is still open.";
const REPEAT_ERROR =
  "You already made this exact call in this turn. Use its earlier result instead of calling it again.";

/**
 * Whether an error is an abort.
 * @param {Error} error - Error.
 * @param {AbortSignal} signal - Turn signal.
 * @returns {boolean} True for user stops.
 */
const isAbort = (error, signal) => signal?.aborted || error?.name === "AbortError";

/**
 * Human error text for a failed turn.
 * @param {Error} error - Error.
 * @returns {string} Message.
 */
const describeError = (error) => {
  const message = String(error?.message || "");
  if (error?.code === "SAFETY") return "That request was blocked by the AI safety filter. Try rephrasing it.";
  if (error?.code === "MAX_TOKENS") return "That was too large to write in one pass. Try a narrower request (one section, or a selection).";
  if (error?.code === "HTTP_401") return "Your session expired. Sign in again and retry.";
  if (/failed to fetch|network|load failed/i.test(message)) {
    return "I couldn't reach the AI service. Check your connection and try again.";
  }
  if (/safety/i.test(message)) return "That request was blocked by the AI safety filter. Try rephrasing it.";
  if (/max_tokens|too long/i.test(message)) return "The answer ran too long. Ask for a shorter or narrower answer.";
  if (/not authenticated|jwt/i.test(message)) return "Your session expired. Sign in again and retry.";
  return message || "Something went wrong while answering. Try again.";
};

/**
 * Compact args for persistence (long strings trimmed).
 * @param {object} args - Tool args.
 * @returns {object} Compact args.
 */
const compactArgs = (args) => {
  const out = {};
  for (const [key, value] of Object.entries(args || {})) {
    out[key] = typeof value === "string" && value.length > 600 ? `${value.slice(0, 600)}…` : value;
  }
  return out;
};

/**
 * Safe human label for a step.
 * @param {object} tool - Registry tool.
 * @param {object} args - Args.
 * @returns {string} Label.
 */
const stepLabel = (tool, args) => {
  try {
    return tool.label?.(args) || tool.name;
  } catch {
    return tool.name;
  }
};

let stepSeq = 0;
let cardSeq = 0;

/**
 * Run one Inky turn.
 * @param {object} params - Turn inputs.
 * @param {string} params.key - Turn key (assistantTurns.newTurnKey()).
 * @param {string} params.text - The user's message.
 * @param {Array<object>} params.tools - Tool registry.
 * @param {object} params.runtime - Live accessors (see ChatAgentPanel): saveMessage, getMessages,
 *   getConversationTitle, updateConversationTitle, getContext, getEditorRef, accountId,
 *   conversationId, newConversationTitle.
 * @param {string} [params.origin] - Surface that started the turn.
 * @param {object|null} [params.existingUserMessage] - Saved user message (retry/regenerate: not saved again).
 * @param {number} [params.variant] - Regenerate counter (changes the seed on purpose).
 * @returns {Promise<void>} Resolves when the turn settles (never rejects).
 */
export const runAssistantTurn = async ({
  key,
  text,
  tools,
  runtime,
  origin = "dock",
  existingUserMessage = null,
  variant = 0,
}) => {
  const controller = beginTurn({
    key,
    origin,
    conversationId: existingUserMessage?.conversation_id || runtime.conversationId || null,
    userMessage: { content: text },
    userMessageId: existingUserMessage?.id || null,
  });
  if (runtime.serverAgent !== false && serverAgentEnabled()) {
    try {
      const outcome = await runServerTurn({ key, text, signal: controller.signal, runtime, existingUserMessage, variant });
      if (outcome !== "fallback") return;
    } catch (error) {
      console.error("[inky] server turn failed", error);
      failTurn(key, describeError(error), { retryable: true, retry: { text, userMessage: existingUserMessage, variant } });
      return;
    }
  }
  await runBrowserTurn({ key, text, tools, runtime, existingUserMessage, variant, controller });
};

/**
 * The in-browser loop (used when the server agent is unavailable).
 * @param {object} params - Turn inputs (see {@link runAssistantTurn}) plus the turn's controller.
 * @returns {Promise<void>} Resolves when the turn settles (never rejects).
 */
const runBrowserTurn = async ({ key, text, tools, runtime, existingUserMessage = null, variant = 0, controller }) => {
  const signal = controller.signal;
  const textBuffer = createFrameTextBuffer((delta) => applyTurnEvent(key, { type: "text_delta", delta }));
  const startedAt = Date.now();
  const steps = [];
  const cards = [];
  let userSaved = existingUserMessage;
  let conversationId = existingUserMessage?.conversation_id || runtime.conversationId || null;
  let partialText = "";
  let protocol = "native";
  let model = null;

  const retryInfo = () => ({ retry: { text, userMessage: userSaved, variant } });

  /**
   * Visible text of the current model round: kept for a stopped reply and pushed to the
   * frame-coalesced buffer.
   * @param {string} delta - Text delta.
   * @returns {void}
   */
  const onRoundText = (delta) => {
    partialText += delta;
    textBuffer.push(delta);
  };

  /**
   * Execute one model-requested call and record its step/card.
   * @param {{name: string, args: object}} call - Function call.
   * @param {object} availability - Availability context.
   * @returns {Promise<object>} functionResponse payload.
   */
  const executeCall = async (call, availability) => {
    const tool = findTool(tools, call.name);
    if (!tool) {
      return { error: `Unknown tool "${call.name}". Available: ${tools.filter((t) => isToolAvailable(t, availability)).map((t) => toGeminiName(t.name)).join(", ")}.` };
    }
    if (!isToolAvailable(tool, availability)) {
      return { error: `${toGeminiName(tool.name)} is not available right now${tool.name.startsWith("doc.") ? " (no document or template is open)" : ""}.` };
    }
    const validation = validateArgs(tool.parameters, call.args);
    if (!validation.ok) {
      return { error: "Invalid arguments.", problems: validation.errors };
    }
    const args = validation.value;
    stepSeq += 1;
    const id = `step-${Date.now().toString(36)}-${stepSeq}`;
    applyTurnEvent(key, { type: "step_start", step: { id, tool: tool.name, label: stepLabel(tool, args) } });
    const stepStartedAt = Date.now();

    let result;
    try {
      result = await tool.run(args, {
        signal,
        variant,
        conversationId,
        onProgress: (label) => label && applyTurnEvent(key, { type: "step_update", id, label }),
        markNavigated: () => applyTurnEvent(key, { type: "navigated" }),
      });
    } catch (error) {
      if (isAbort(error, signal)) throw error;
      console.error(`[inky] ${tool.name} failed`, error);
      result = { ok: false, summary: "Failed", data: { error: describeError(error) } };
    }
    if (signal.aborted) throw new DOMException("Stopped", "AbortError");

    const ok = result?.ok !== false;
    let cardId = null;
    if (result?.card) {
      cardSeq += 1;
      cardId = `card-${Date.now().toString(36)}-${cardSeq}`;
      cards.push({ id: cardId, tool: tool.name, ...result.card });
    }
    steps.push({
      id,
      name: tool.name,
      label: stepLabel(tool, args),
      args: compactArgs(args),
      ok,
      summary: oneLine(result?.summary || (ok ? "Done" : "Failed"), 120),
      result: capResultText(result?.data ?? null, TOOL_RESULT_PERSIST_CHARS),
      ms: Date.now() - stepStartedAt,
      ...(cardId ? { cardId } : {}),
    });
    applyTurnEvent(key, { type: "step_end", id, status: ok ? "done" : "error", summary: result?.summary });
    return toFunctionResponsePayload(result?.data ?? { ok });
  };

  try {
    // 1. Persist the user message first; its row tells us the conversation (P1).
    if (!userSaved) {
      userSaved = await runtime.saveMessage(
        { role: "user", content: text },
        null,
        null,
        null,
        { conversationId, title: runtime.newConversationTitle || undefined }
      );
    }
    conversationId = userSaved?.conversation_id || conversationId;
    applyTurnEvent(key, { type: "conversation", conversationId, userMessageId: userSaved?.id || null });
    if (signal.aborted) throw new DOMException("Stopped", "AbortError");

    const history = (runtime.getMessages(conversationId) || []).filter(
      (message) => message.id !== userSaved?.id && message.sequence_number < (userSaved?.sequence_number ?? Infinity)
    );
    const turnIndex = history.filter((message) => message.role === "user").length + 1;
    const initialContext = runtime.getContext();
    const initialActive = resolveActiveContext({
      pathname: initialContext.pathname,
      search: initialContext.search,
      contextData: initialContext.contextData,
    });

    // 2. Retrieval once per turn, keyed on the user's own words (P7), in parallel with the probe.
    const [retrieved, caps] = await Promise.all([
      retrieveForTurn({ projectId: initialActive.project?.id || null, query: text }),
      chatAgentService.getCapabilities(),
    ]);
    protocol = caps.native ? "native" : "legacy";

    // What the user is looking at. Re-read between rounds when a tool changed it (Inky opened
    // a document, say), so the prompt and the offered tools (doc_edit) follow the page.
    let availability;
    let offered;
    let promptInputs;
    let surfaceKey = null;
    /**
     * Rebuild the per-surface inputs when the route or the open editor changed.
     * @returns {boolean} True when they were rebuilt.
     */
    const refreshSurface = () => {
      const context = runtime.getContext();
      const editorRef = runtime.getEditorRef?.()?.current || null;
      let editorMeta = null;
      try {
        editorMeta = editorRef?.getMeta?.() || null;
      } catch {
        editorMeta = null;
      }
      const nextKey = [
        context.pathname,
        context.search,
        editorRef ? "editor" : "none",
        editorMeta?.documentId || editorMeta?.templateId || "",
      ].join("|");
      if (nextKey === surfaceKey) return false;
      surfaceKey = nextKey;
      const active = resolveActiveContext({ pathname: context.pathname, search: context.search, contextData: context.contextData });
      availability = { active };
      const editor = snapshotEditor(runtime.getEditorRef, {
        fallbackMeta: active.kind === "document"
          ? { kind: "document", documentId: active.document?.id || null, title: active.document?.name || "Document" }
          : active.kind === "template"
            ? { kind: "template", templateId: active.template?.id || null, title: active.template?.name || "Template" }
            : null,
      });
      offered = tools.filter((tool) => isToolAvailable(tool, availability));
      promptInputs = {
        contextSummary: context.contextSummary,
        contextData: context.contextData,
        surface: editor ? { ...editor, project: active.kind === "document" ? active.project : null } : null,
        retrievedContext: retrieved.section,
      };
      return true;
    };
    refreshSurface();

    let finalText = "";
    let finishReason = null;

    const runNative = async () => {
      let systemInstruction = buildInkySystemPrompt(promptInputs);
      let declarations = buildFunctionDeclarations(offered, availability);
      const contents = buildHistoryContents(history, { knownNames: new Set(offered.map((tool) => toGeminiName(tool.name))) });
      pushContent(contents, "user", [{ text }, ...(retrieved.imageParts || [])]);
      const seen = new Set();
      let repeats = 0;
      let emptyRetried = false;
      let textOnly = false;

      for (let round = 0; round <= MAX_ROUNDS; round += 1) {
        if (round === MAX_ROUNDS && !textOnly) {
          textOnly = true;
          pushContent(contents, "user", [{ text: BUDGET_NUDGE }]);
        }
        applyTurnEvent(key, { type: "phase", phase: "thinking" });
        partialText = "";
        const response = await chatAgentService.callNative({
          contents,
          systemInstruction,
          functionDeclarations: textOnly ? [] : declarations,
          toolConfig: textOnly ? { functionCallingConfig: { mode: "NONE" } } : undefined,
          seed: stableSeed("chat.agent", conversationId || "new", String(turnIndex), String(variant), String(round)),
          accountId: runtime.accountId,
          signal,
          onText: onRoundText,
        });
        textBuffer.flush();
        model = response.model || model;
        finishReason = response.finishReason || null;
        const calls = textOnly ? [] : response.functionCalls || [];

        if (calls.length) {
          // Narration before tool calls is transient: the reply is the final round's text.
          textBuffer.reset();
          applyTurnEvent(key, { type: "text_reset" });
          // Prefer the proxy's model turn verbatim: it carries the thought signatures Gemini 3+
          // validates on the next request. Otherwise rebuild it from the calls.
          const echoed = response.modelContent?.parts?.some((part) => part?.functionCall) ? response.modelContent.parts : null;
          const modelParts = echoed || [];
          if (!echoed) {
            if (response.text) modelParts.push({ text: response.text });
            calls.forEach((call, index) => {
              const signature = call.thoughtSignature || (index === 0 ? REPLAYED_CALL_SIGNATURE : null);
              modelParts.push({ functionCall: { name: call.name, args: call.args || {} }, ...(signature ? { thoughtSignature: signature } : {}) });
            });
          }
          pushContent(contents, "model", modelParts);
          const responses = [];
          for (const call of calls) {
            const sig = callSignature(call.name, call.args);
            let payload;
            if (seen.has(sig)) {
              repeats += 1;
              payload = { error: REPEAT_ERROR };
            } else {
              seen.add(sig);
              payload = await executeCall(call, availability);
            }
            responses.push({ functionResponse: { name: call.name, response: payload } });
          }
          pushContent(contents, "user", responses);
          if (repeats >= 2) textOnly = true;
          if (refreshSurface()) {
            systemInstruction = buildInkySystemPrompt(promptInputs);
            declarations = buildFunctionDeclarations(offered, availability);
          }
          continue;
        }

        const replyText = String(response.text || partialText || "").trim();
        if (!replyText && !emptyRetried && round < MAX_ROUNDS) {
          emptyRetried = true;
          pushContent(contents, "user", [{ text: EMPTY_RETRY_NUDGE }]);
          continue;
        }
        finalText = replyText;
        break;
      }
    };

    const runLegacy = async () => {
      protocol = "legacy";
      const legacyPrompt = () =>
        buildInkySystemPrompt({
          ...promptInputs,
          legacy: true,
          legacyToolsJson: JSON.stringify(compactToolsForPrompt(offered), null, 1),
        });
      let systemInstruction = legacyPrompt();
      const working = [...history, { role: "user", content: text }];
      const seen = new Set();
      for (let round = 0; round < MAX_LEGACY_ROUNDS; round += 1) {
        applyTurnEvent(key, { type: "phase", phase: "thinking" });
        partialText = "";
        const envelope = await chatAgentService.callLegacy({
          prompt: `Conversation:\n${buildLegacyTranscript(working)}\n\nRespond with one JSON object only.`,
          systemInstruction,
          seed: stableSeed("chat.agent", conversationId || "new", String(turnIndex), String(variant), String(round)),
          accountId: runtime.accountId,
          signal,
          onText: onRoundText,
        });
        textBuffer.flush();
        if (envelope.type === "tool_call") {
          textBuffer.reset();
          applyTurnEvent(key, { type: "text_reset" });
          const sig = callSignature(envelope.toolName, envelope.args);
          const payload = seen.has(sig) ? { error: REPEAT_ERROR } : await executeCall({ name: envelope.toolName, args: envelope.args }, availability);
          seen.add(sig);
          working.push(
            { role: "assistant", content: `[Calling ${envelope.toolName}]` },
            { role: "user", content: `[Tool result for ${envelope.toolName}]: ${capResultText(payload)}` }
          );
          if (refreshSurface()) systemInstruction = legacyPrompt();
          continue;
        }
        finalText = String(envelope.content || "").trim();
        break;
      }
    };

    if (protocol === "native") {
      try {
        await runNative();
      } catch (error) {
        if (isAbort(error, signal) || !chatAgentService.degradeOnLegacyError(error)) throw error;
        steps.length = 0;
        cards.length = 0;
        await runLegacy();
      }
    } else {
      await runLegacy();
    }

    if (!finalText) {
      finalText = steps.length
        ? "I ran the steps above but couldn't write a summary for this turn. Ask me to continue and I'll pick up from them."
        : "I couldn't come up with a reply to that. Could you rephrase it?";
    } else if (finishReason === "MAX_TOKENS") {
      finalText = `${finalText}\n\n_(The answer was cut off. Ask me to continue.)_`;
    }

    // 3. Persist the reply with its trace and cards into the SAME conversation.
    const toolRecord = steps.length || cards.length
      ? { v: 2, steps, cards, protocol, model, durationMs: Date.now() - startedAt }
      : null;
    const saved = await runtime.saveMessage({ role: "assistant", content: finalText }, null, null, toolRecord, { conversationId });
    finishTurn(key, { messageId: saved?.id || null });

    // 4. Name a fresh general conversation after its first exchange (best effort, fast task).
    if (turnIndex === 1 && conversationId && runtime.getConversationTitle(conversationId) === DEFAULT_CONVERSATION_TITLE) {
      chatAgentService
        .suggestTitle({ userText: text, replyText: finalText, seed: stableSeed("chat.title", conversationId) })
        .then((title) => title && runtime.updateConversationTitle(conversationId, title))
        .catch(() => {});
    }
  } catch (error) {
    textBuffer.cancel();
    if (isAbort(error, signal)) {
      let messageId = null;
      const partial = partialText.trim();
      if (userSaved && (partial || steps.length || cards.length)) {
        try {
          const saved = await runtime.saveMessage(
            { role: "assistant", content: partial ? `${partial}\n\n_Stopped._` : "_Stopped._" },
            null,
            null,
            steps.length || cards.length ? { v: 2, steps, cards, protocol, model, stopped: true, durationMs: Date.now() - startedAt } : null,
            { conversationId }
          );
          messageId = saved?.id || null;
        } catch (saveError) {
          console.warn("[inky] could not save the stopped reply", saveError);
        }
      }
      markTurnStopped(key, { messageId, ...retryInfo() });
      return;
    }
    console.error("[inky] turn failed", error);
    failTurn(key, describeError(error), { retryable: true, ...retryInfo() });
  }
};

/**
 * Commit (or cancel) a pending approval card after the user acts on it, and persist the
 * card's new state on its message so later turns see the outcome.
 * @param {object} params - Inputs.
 * @param {Array<object>} params.tools - Tool registry.
 * @param {object} params.message - Saved assistant message carrying the card.
 * @param {string} params.cardId - Card id.
 * @param {'commit'|'cancel'|'update'|'undo'} params.action - User action ('update' merges `values` into the card, e.g. a review status; 'undo' reverses a done card whose tool supports it).
 * @param {object} [params.values] - Edited card values (commit) or the card patch (update).
 * @param {(messageId: string, fields: object) => Promise<object>} params.updateMessage - ChatMemory updateMessage.
 * @returns {Promise<{ok: boolean, error?: string}>} Outcome.
 */
export const resolveCard = async ({ tools, message, cardId, action, values, updateMessage }) => {
  const record = message?.tool_call;
  const card = record?.cards?.find((item) => item.id === cardId);
  if (!card) return { ok: false, error: "This card is no longer available." };
  // Claim guard: a decided card is never committed twice (two tabs, a double click).
  if ((action === "commit" || action === "cancel") && (card.status === "done" || card.status === "cancelled")) {
    return { ok: false, error: card.status === "done" ? "This was already applied." : "This was already cancelled." };
  }

  const persist = async (patch) => {
    const nextRecord = { ...record, cards: record.cards.map((item) => (item.id === cardId ? { ...item, ...patch } : item)) };
    try {
      await updateMessage(message.id, { tool_call: nextRecord });
    } catch (error) {
      console.warn("[inky] could not persist card state", error);
    }
  };

  if (action === "cancel") {
    await persist({ status: "cancelled", outcome: "The user cancelled this." });
    return { ok: true };
  }
  if (action === "undo") {
    if (card.status !== "done") return { ok: false, error: card.status === "undone" ? "This was already undone." : "Only a finished action can be undone." };
    const undoTool = findTool(tools, card.tool);
    if (!undoTool || typeof undoTool.undo !== "function") return { ok: false, error: "This action cannot be undone." };
    try {
      const result = await undoTool.undo(card);
      await persist({ status: "undone", outcome: result?.outcome || "Undone." });
      return { ok: true };
    } catch (error) {
      return { ok: false, error: describeError(error) };
    }
  }
  if (action !== "commit") {
    await persist({ ...(values || {}) });
    return { ok: true };
  }

  const tool = findTool(tools, card.tool);
  if (!tool || typeof tool.commit !== "function") return { ok: false, error: "This action can no longer be completed." };
  try {
    const result = await tool.commit({ ...(card.values || {}), ...(values || {}) }, { card });
    await persist({
      status: "done",
      values: { ...(card.values || {}), ...(values || {}) },
      outcome: result.outcome || result.summary,
      entity: result.entity || null,
      ...(Array.isArray(result.results) ? { results: result.results } : {}),
    });
    return { ok: true };
  } catch (error) {
    return { ok: false, error: describeError(error) };
  }
};
